/**
 * Admissions and the two selection registers: the qualifications a review
 * granted, the choices they qualify, and the selection policies and bundles
 * that pick among them, organisation-wide or for one project.
 */

import type {
  ApprovedChoicesUpdate,
  BundleCandidatesResponse,
  BundleChoice,
  BundleEligibilityResponse,
  BundleProposalResponse,
  GovernanceUpdate,
  QualificationExpireRequest,
  QualificationExpiryResponse,
  QualificationFilters,
  QualificationListResponse,
  QualifiedChoicesResponse,
  SelectChoiceRequest,
  SelectionBundle,
  SelectionPolicy,
  SelectionUpdate,
} from '@/types/modelCapabilities'
import { apiClient } from './client'
import { ifMatchHeaders } from './ifMatch'
import { BASE, scoped } from './modelCapabilityPaths'

export const modelCapabilitySelection = {
  listQualifications: async (
    filters: QualificationFilters = {}
  ): Promise<QualificationListResponse> => {
    const response = await apiClient.get<QualificationListResponse>(
      `${BASE}/qualifications/`,
      { params: filters }
    )
    return response.data
  },

  // Answers with the ids it actually retired: an admission a concurrent
  // review already superseded is left alone and the list comes back empty.
  expireQualification: async (
    qualificationId: string,
    body: QualificationExpireRequest,
    ifMatch: number
  ): Promise<QualificationExpiryResponse> => {
    const response = await apiClient.post<QualificationExpiryResponse>(
      `${BASE}/qualifications/${qualificationId}/expire/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  getQualifiedChoices: async (
    capabilityKey: string,
    projectId?: string
  ): Promise<QualifiedChoicesResponse> => {
    const response = await apiClient.get<QualifiedChoicesResponse>(
      `${scoped(projectId)}/qualified-choices/${capabilityKey}/`
    )
    return response.data
  },

  getSelectionPolicy: async (
    capabilityKey: string,
    projectId?: string
  ): Promise<SelectionPolicy> => {
    const response = await apiClient.get<SelectionPolicy>(
      `${scoped(projectId)}/selection-policies/${capabilityKey}/`
    )
    return response.data
  },

  updatePolicyGovernance: async (
    capabilityKey: string,
    body: GovernanceUpdate,
    ifMatch: number,
    projectId?: string
  ): Promise<SelectionPolicy> => {
    const response = await apiClient.put<SelectionPolicy>(
      `${scoped(projectId)}/selection-policies/${capabilityKey}/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  updatePolicySelection: async (
    capabilityKey: string,
    body: SelectionUpdate,
    ifMatch: number,
    projectId?: string
  ): Promise<SelectionPolicy> => {
    const response = await apiClient.put<SelectionPolicy>(
      `${scoped(projectId)}/selection-policies/${capabilityKey}/selection/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  getSelectionBundle: async (
    bundleKey: string,
    projectId?: string
  ): Promise<SelectionBundle> => {
    const response = await apiClient.get<SelectionBundle>(
      `${scoped(projectId)}/selection-bundles/${bundleKey}/`
    )
    return response.data
  },

  getBundleCandidates: async (
    bundleKey: string,
    projectId?: string
  ): Promise<BundleCandidatesResponse> => {
    const response = await apiClient.get<BundleCandidatesResponse>(
      `${scoped(projectId)}/selection-bundles/${bundleKey}/candidates/`
    )
    return response.data
  },

  // Served at this one path, with no project family, and carries no
  // `permissions`: it advises, and the bundle row still decides who may act.
  getBundleProposal: async (
    bundleKey: string
  ): Promise<BundleProposalResponse> => {
    const response = await apiClient.get<BundleProposalResponse>(
      `${BASE}/selection-bundles/${bundleKey}/proposal/`
    )
    return response.data
  },

  approveBundleChoices: async (
    bundleKey: string,
    body: ApprovedChoicesUpdate,
    ifMatch: number,
    projectId?: string
  ): Promise<SelectionBundle> => {
    const response = await apiClient.put<SelectionBundle>(
      `${scoped(projectId)}/selection-bundles/${bundleKey}/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  selectBundleChoice: async (
    bundleKey: string,
    body: SelectChoiceRequest,
    ifMatch: number,
    projectId?: string
  ): Promise<SelectionBundle> => {
    const response = await apiClient.put<SelectionBundle>(
      `${scoped(projectId)}/selection-bundles/${bundleKey}/selection/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  // The dry run: writes nothing and answers the structured per-role verdict,
  // so the only 422 the write itself can produce is the flat field-prefixed
  // shape the shared error table already routes inline.
  validateBundle: async (
    bundleKey: string,
    body: BundleChoice,
    projectId?: string
  ): Promise<BundleEligibilityResponse> => {
    const response = await apiClient.post<BundleEligibilityResponse>(
      `${scoped(projectId)}/selection-bundles/${bundleKey}/validate/`,
      body
    )
    return response.data
  },
}
